import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Top-of-app banner that reflects the current subscription state.
 * Hidden entirely for admin/exempt accounts and users with >7 trial days.
 *
 * States:
 *  - trial (<=7 days left)   -> soft blue nudge
 *  - trial (last day)        -> amber
 *  - unpaid / expired        -> red read-only warning
 */
export default function SubscriptionBanner() {
  const [state, setState] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/billing/status`, { withCredentials: true });
        if (alive) setState(data);
      } catch (_) { /* silent — user is logged out */ }
    })();
    return () => { alive = false; };
  }, []);

  if (!state) return null;
  if (state.status === 'exempt' || state.status === 'active') return null;
  if (state.status === 'trial' && state.days_remaining > 7) return null;

  const isBlocked = state.status === 'unpaid' || state.status === 'expired';
  const tone = isBlocked
    ? 'bg-red-50 border-red-300 text-red-800'
    : (state.days_remaining <= 1 ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-blue-50 border-blue-300 text-blue-800');
  const Icon = isBlocked ? AlertTriangle : Sparkles;

  return (
    <div
      data-testid="subscription-banner"
      className={`border-b px-4 py-2 flex items-center justify-between gap-3 text-sm ${tone}`}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 shrink-0" />
        <span>
          {isBlocked
            ? `${state.message} — your existing data is safe to view, but you can’t add or edit until you subscribe.`
            : `${state.message}. Subscribe now to keep working without interruption.`}
        </span>
      </div>
      <Link
        to="/billing"
        data-testid="subscription-banner-cta"
        className="shrink-0 px-3 py-1 rounded-md bg-white/70 hover:bg-white border border-current font-semibold"
      >
        {isBlocked ? 'Subscribe →' : 'View plans →'}
      </Link>
    </div>
  );
}
